import { track } from '@/lib/analytics/track';
import { TrackingEventName } from '@/models/analytics/eventTypes';
import { ButtonAction, ScreenName } from '@heidi/analytics/vocabulary';
import {
  Banner,
  BannerAction,
  BannerContent,
  BannerTitle,
} from '@heidi/design-system/banner';
import { Button } from '@heidi/design-system/button';
import { useTranslations } from '@heidi/runtime/i18n';
import { GraduationCap } from 'lucide-react';

const TRAINEE_SIGN_UP_URL = 'https://www.heidihealth.com/trainee-sign-up';

type TrialExtensionTraineeVerificationBannerProps = {
  trialDaysRemaining: number;
};

export function TrialExtensionTraineeVerificationBanner({
  trialDaysRemaining,
}: TrialExtensionTraineeVerificationBannerProps) {
  const { t } = useTranslations();

  const handleVerifyClick = () => {
    track({
      event_name: TrackingEventName.ButtonClicked,
      props: {
        screen_name: ScreenName.TrialExtensionModal,
        action: ButtonAction.StudentVerificationCtaClicked,
        label: 'Verify as a student or trainee for free access',
        trial_days_remaining: trialDaysRemaining,
      },
    });
    window.open(TRAINEE_SIGN_UP_URL, '_blank', 'noopener,noreferrer');
  };

  return (
    <Banner
      subVariant="subtle"
      className="w-full flex-wrap items-center gap-y-3"
      data-testid="trial-extension-trainee-verification-banner"
    >
      <BannerContent
        className="min-w-0 flex-1 items-center"
        icon={<GraduationCap aria-hidden className="shrink-0 text-text-primary" />}
      >
        <BannerTitle className="text-pretty">
          {t('trialExtension.traineeVerificationBannerTitle')}
        </BannerTitle>
      </BannerContent>
      <BannerAction className="ms-auto shrink-0">
        <Button
          type="button"
          variant="secondary"
          size="xs"
          aria-label={t('trialExtension.verifyTraineeForFreeAccess')}
          data-testid="trial-extension-trainee-verification-cta"
          onClick={handleVerifyClick}
        >
          {t('trialExtension.traineeVerificationBannerVerify')}
        </Button>
      </BannerAction>
    </Banner>
  );
}
